import { useInfiniteQuery } from '@tanstack/react-query';

import { expectStatus } from '@/lib/api/client';
import {
  getReadWorkspaceAccessLogQueryKey,
  readWorkspaceAccessLog,
} from '@/lib/api/generated/api';
import type { AccessLogPage } from '@/lib/api/generated/model';

/**
 * A workspace's access log, newest first, a page at a time (spec 191). Asked only
 * when `enabled`: the service answers it to owners and org admins alone.
 */
export function useWorkspaceAccessLog(workspaceId: string, enabled: boolean) {
  const query = useInfiniteQuery({
    queryKey: getReadWorkspaceAccessLogQueryKey(workspaceId),
    queryFn: async ({ pageParam, signal }): Promise<AccessLogPage> =>
      expectStatus(
        await readWorkspaceAccessLog(
          workspaceId,
          pageParam ? { before: pageParam } : undefined,
          { signal },
        ),
        200,
        'The activity could not be read.',
      ),
    initialPageParam: null as string | null,
    getNextPageParam: (last) => last.next_before ?? null,
    enabled,
  });
  return {
    entries: query.data?.pages.flatMap((page) => page.items) ?? [],
    isPending: enabled && query.isPending,
    isError: query.isError,
    hasMore: query.hasNextPage,
    loadingMore: query.isFetchingNextPage,
    loadMore: () => void query.fetchNextPage(),
    retry: () => void query.refetch(),
  };
}
